import {
  CaretDownIcon,
  CheckIcon,
  MinusIcon,
  SpinnerGapIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import { COLLAPSIBLE_MOTION, ITEM_META, ITEM_TITLE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { describeModelFailure } from '@/lib/workflows/steps/transient-model-error';
import {
  type ArticleReviewJob,
  articleStageLabel,
  isArticleJobActive,
} from '@/services/data/ArticleJobService';

type ArticleJobStep = ArticleReviewJob['steps'][number];

const STEP_LABEL: Record<string, string> = {
  pending: 'Waiting',
  running: 'In progress',
  succeeded: 'Done',
  skipped: 'Not needed',
  failed: 'Failed',
  cancelled: 'Stopped',
};

const STEP_TONE: Record<string, StatusTone> = {
  pending: 'neutral',
  running: 'info',
  succeeded: 'success',
  skipped: 'neutral',
  failed: 'destructive',
  cancelled: 'neutral',
};

/**
 * Durable-workflow failures arrive with the internal step path and retry count
 * glued to the front — `Step "step//./src/lib/workflows/steps/article/generation//
 * writeRevision" failed after 0 retries: Resource exhausted.` — and this row
 * rendered the whole string verbatim to the user. Strip the frame, then say what
 * the provider error actually means for the article.
 */
function readableFinding(finding: string): string {
  // Only a raw runtime frame is rewritten. A message Mora composed ("The first
  // draft is saved…") is already written for a person and passes through.
  if (!/^\s*Step\s+"/i.test(finding)) return finding;
  return `This stopped because ${describeModelFailure(finding)}. Try again — it usually clears on its own.`;
}

/**
 * What the progress card says under a run that ended badly.
 *
 * A run's OWN reason comes first when it failed or never produced an article.
 * `partialMessage` is the dock's summary of the article's open review notes —
 * true, but about the article that already existed, not about this run. Shown
 * first, it hid why "Rewrite with verified sources" stopped: research ran out
 * of credible sources and the card said "3 editorial findings need a decision"
 * (handoff 2026-10-15 §3.5).
 */
export function articleJobOutcomeMessage(
  job: Pick<ArticleReviewJob, 'status' | 'operation' | 'errorMessage'>,
  partialMessage: string | null,
): string {
  const reason = job.errorMessage ?? '';
  if (/evidence budget/i.test(reason)) {
    return job.operation === 'draft'
      ? 'Research could not find enough credible sources to write from, so no article was written. Sources it read are saved. Try again; if it fails twice, reword the topic so it names something a source can speak to.'
      : 'Research could not find enough credible sources for this rewrite, so the article was not changed. Sources it read are saved. Try again, or narrow the request to the claims that need a source.';
  }
  if (job.status === 'failed') {
    return reason
      ? readableFinding(reason)
      : (partialMessage ?? 'The request did not finish. Any saved article remains available.');
  }
  return (
    partialMessage ||
    (reason ? readableFinding(reason) : null) ||
    'The request did not finish. Any saved article remains available.'
  );
}

function StepIndicator({ step, position }: { step: ArticleJobStep; position: number }) {
  const shell =
    'flex size-6 shrink-0 items-center justify-center rounded-full border text-micro font-medium tabular-nums';
  if (step.status === 'running') {
    return (
      <span className={cn(shell, 'border-info/30 text-info')} aria-hidden="true">
        <SpinnerGapIcon className="size-3.5 animate-spin motion-reduce:animate-none" />
      </span>
    );
  }
  if (step.status === 'succeeded') {
    return (
      <span
        className={cn(shell, 'border-success/30 bg-success/10 text-success')}
        aria-hidden="true"
      >
        <CheckIcon className="size-3.5" weight="bold" />
      </span>
    );
  }
  if (step.status === 'failed') {
    return (
      <span
        className={cn(shell, 'border-destructive/30 bg-destructive/10 text-destructive')}
        aria-hidden="true"
      >
        <WarningIcon className="size-3.5" weight="bold" />
      </span>
    );
  }
  if (step.status === 'skipped' || step.status === 'cancelled') {
    return (
      <span className={cn(shell, 'border-border/60 text-muted-foreground')} aria-hidden="true">
        <MinusIcon className="size-3.5" />
      </span>
    );
  }
  return (
    <span className={cn(shell, 'border-border/60 text-muted-foreground')} aria-hidden="true">
      {position}
    </span>
  );
}

function ArticleTaskRow({ step, position }: { step: ArticleJobStep; position: number }) {
  const [open, setOpen] = useState(step.status === 'failed');
  const hasDetail = Boolean(step.finding?.trim());
  const summary = (
    <>
      <StepIndicator step={step} position={position} />
      {/* ONE LINE PER STEP (Task Rows). The label truncates with an ellipsis and
          the status sits at the end, never squeezed. The earlier `text-balance`
          wrap fixed a mid-word clip against a fade, but in an ~800px document
          pane the rail is ~240px wide and the label wrapped one word per line
          ("Find / the / review / notes"). The full label stays in `title` and,
          for rows with detail, in the trigger's accessible name. */}
      <span
        title={step.label}
        className={cn(ITEM_TITLE, 'block min-w-0 flex-1 truncate whitespace-nowrap text-left')}
      >
        {step.label}
      </span>
      <StatusBadge
        tone={STEP_TONE[step.status] ?? 'neutral'}
        className="shrink-0 whitespace-nowrap"
      >
        {STEP_LABEL[step.status] ?? 'Unknown'}
      </StatusBadge>
      {hasDetail ? (
        <CaretDownIcon
          aria-hidden="true"
          className={cn(
            'size-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ease-fluid',
            open && 'rotate-180',
          )}
        />
      ) : null}
    </>
  );

  if (!hasDetail) {
    return <li className="flex min-h-11 items-center gap-2.5 px-3">{summary}</li>;
  }

  return (
    <li>
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className={cn('overflow-hidden transition-colors', open && 'bg-muted/30')}>
          <CollapsibleTrigger
            aria-label={`${step.label} — ${STEP_LABEL[step.status] ?? 'Unknown'}. Show details.`}
            className="flex min-h-11 w-full items-center gap-2.5 px-3"
          >
            {summary}
          </CollapsibleTrigger>
          <CollapsibleContent className={COLLAPSIBLE_MOTION}>
            <Separator className="bg-border/30" />
            <p className={cn(ITEM_META, 'px-11 py-2.5 leading-relaxed')}>
              {readableFinding(step.finding ?? '')}
            </p>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </li>
  );
}

export function ArticleJobProgress({
  job,
  stopping,
  onStop,
  partialMessage,
  actionRequired = true,
  onRetry,
  retryLabel = 'Try again',
  retrying = false,
}: {
  /**
   * Repeats the run that just ended. A failed, stopped or unproductive run used
   * to end on a message and nothing else — the person had to guess which of
   * Write / Improve / Recheck would pick it back up. Omit when a retry would
   * not change the outcome.
   */
  onRetry?: () => void;
  retryLabel?: string;
  retrying?: boolean;
  /** `null` suppresses a historical partial status when the dock has a more precise current verdict. */
  partialMessage?: string | null;
  /**
   * A partial job can mean either actionable findings or an honest review
   * limitation. The latter must not contradict a current "Ready to hand off"
   * verdict by calling the article "Needs attention".
   */
  actionRequired?: boolean;
  job: ArticleReviewJob;
  stopping: boolean;
  onStop: () => Promise<unknown>;
}) {
  const active = isArticleJobActive(job);
  const finishedSteps = job.steps.filter((step) =>
    ['succeeded', 'skipped', 'failed', 'cancelled'].includes(step.status),
  ).length;
  const progressLabel =
    job.operation === 'factcheck'
      ? 'Claim check progress'
      : job.operation === 'address'
        ? 'Review notes progress'
        : job.operation === 'recheck'
          ? 'Check progress'
          : job.operation === 'revise'
            ? 'Improvement progress'
            : 'Writing progress';
  const settledClean =
    !active &&
    (job.status === 'succeeded' || job.status === 'partial') &&
    job.steps.length > 0 &&
    job.steps.every((step) => step.status === 'succeeded' || step.status === 'skipped');
  const currentStepIndex = job.steps.findIndex(
    (step) => step.stepKey === job.currentStage || step.status === 'running',
  );
  const settledLabel =
    job.status === 'partial' && !actionRequired ? 'Review complete' : articleStageLabel(job);
  // A settled run that finished every step says nothing about counts: the article ring beside it
  // already counts the article's stages, and "5 of 5" next to "1 of 6" read as two conflicting meters.
  // A run that stopped short keeps its count, labelled as the run's own steps.
  const progressSummary =
    job.steps.length === 0
      ? settledLabel
      : active
        ? `${articleStageLabel(job)} · Step ${Math.max(1, currentStepIndex + 1)} of ${job.steps.length}`
        : finishedSteps === job.steps.length
          ? settledLabel
          : `${settledLabel} · ${finishedSteps} of ${job.steps.length} run steps finished`;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-caption font-medium text-foreground/85">{progressLabel}</p>
          <p role={active ? 'status' : undefined} className={ITEM_META}>
            {progressSummary}
          </p>
        </div>
        {active ? (
          <Button
            variant="flat"
            size="sm"
            disabled={stopping || job.status === 'cancel_requested'}
            onClick={() =>
              void onStop().catch((error) =>
                toast.error(error instanceof Error ? error.message : 'Could not request a stop.'),
              )
            }
          >
            {stopping ? 'Requesting…' : 'Stop'}
          </Button>
        ) : null}
      </div>
      {job.status === 'failed' ||
      (job.status === 'partial' &&
        (partialMessage !== null || /evidence budget/i.test(job.errorMessage ?? ''))) ? (
        <p
          className={
            job.status === 'failed'
              ? 'text-caption text-destructive'
              : actionRequired
                ? 'text-caption text-warning'
                : ITEM_META
          }
        >
          {articleJobOutcomeMessage(job, partialMessage)}
        </p>
      ) : null}
      {job.status === 'cancelled' ? (
        <p className={ITEM_META}>Stopped. Any saved article remains available.</p>
      ) : null}
      {onRetry && !active ? (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          disabled={retrying}
          onClick={onRetry}
        >
          {retrying ? 'Starting…' : retryLabel}
        </Button>
      ) : null}
      {job.steps?.length ? (
        // A run that finished every step cleanly folds its steps behind one line: four "Done" rows
        // say nothing the summary has not. A running, failed or stopped run keeps them open, because
        // there the steps are the reason to act.
        <Collapsible defaultOpen={!settledClean}>
          {settledClean ? (
            <CollapsibleTrigger asChild>
              <Button variant="flat" size="sm" className="group -ml-2">
                <CaretDownIcon
                  aria-hidden="true"
                  className="transition-transform duration-200 ease-fluid group-data-[state=closed]:-rotate-90"
                />
                {job.steps.length === 1 ? '1 step' : `${job.steps.length} steps`}
              </Button>
            </CollapsibleTrigger>
          ) : null}
          <CollapsibleContent className={cn(COLLAPSIBLE_MOTION, settledClean && 'pt-2')}>
            {/* One grouped list, not a stack of boxes: the steps are one sequence, so they share
                one edge and split by hairlines. */}
            <ol className="flex flex-col divide-y divide-border/40 overflow-hidden rounded-xl border border-border/50 bg-card/40">
              {job.steps.map((step, index) => (
                <ArticleTaskRow key={step.stepKey} step={step} position={index + 1} />
              ))}
            </ol>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}
